import { PublishedFormId } from '@furnish-hx/constants/FormId';
import FormTypeId from '@core/constants/FormTypeId';
import { MirrorShapeId } from '@core/constants/Shape';
import { MaterialId } from '@core/constants/Material';
import { FieldDimensionId } from '@core/constants/FieldDimension';
import fieldPortfolioData from './common/fieldsPortfolioData';
import {
    IFormFieldKey,
    IForm,
    IFormInputType,
    IDimensionUnit,
} from '../../interfaces/Form';
import fieldsContact from './common/fieldsContact';
import additionalDataFactory from './common/fieldsAdditionalData';
import {
    allMetals,
    allMirror,
    allWoods,
    getMaterialsWithExceptions,
} from './common/materialGroups';

const mirrorForm: IForm = {
    formId       : PublishedFormId.Mirror,
    type         : FormTypeId.GlassAndMirror,
    thumbnailURL : '/resized-images/forms/mirror.jpg',
    fieldsOrder  : [
        IFormFieldKey.PortfolioData,
        IFormFieldKey.Comment,
        IFormFieldKey.MirrorFrameMaterial,
        IFormFieldKey.ShapeDimension,
        IFormFieldKey.Material,
        IFormFieldKey.AdditionalData,
        IFormFieldKey.Contact,
    ],
    fields: {
        portfolioData : fieldPortfolioData,
        comment       : {
            comment: {
                value    : undefined,
                required : false,
            },
        },
        mirrorFrameMaterial: {
            material: {
                value   : undefined,
                options : getMaterialsWithExceptions(
                    [
                        ...allWoods,
                        MaterialId.Ratan,
                        ...allMetals,
                    ],
                    [
                        MaterialId.BlackLocust,
                        MaterialId.Iroko,
                        MaterialId.Douglas,
                        MaterialId.DouglasEngineered,
                        MaterialId.DouglasRecycled,
                        MaterialId.DouglasVeneer,
                        MaterialId.Larch,
                        MaterialId.LarchEngineered,
                        MaterialId.LarchRecycled,
                        MaterialId.LarchVeneer,
                        MaterialId.Padouk,
                        MaterialId.PadoukEngineered,
                        MaterialId.PadoukRecycled,
                        MaterialId.PadoukVeneer,
                        MaterialId.Ipe,
                        MaterialId.IpeRecycled,
                        MaterialId.IpeEngineered,
                        MaterialId.IpeVeneer,
                        MaterialId.Garapa,
                        MaterialId.Rust,
                        MaterialId.SheetMetal,
                        MaterialId.StainlessSteel,
                        MaterialId.Zinc,
                    ],
                ),
                required: false,
            },
        },
        shapeDimension: {
            shape: {
                value   : undefined,
                options : [
                    MirrorShapeId.SquareMirror,
                    MirrorShapeId.RectangleMirror,
                    MirrorShapeId.RoundMirror,
                    MirrorShapeId.OvalMirror,
                ],
                required: true,
            },
            dimension: {
                options: {
                    [MirrorShapeId.SquareMirror]: {
                        [FieldDimensionId.Width]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 50, max: 1000 },
                            required     : true,
                            defaultValue : 200,
                        },
                        [FieldDimensionId.Thickness]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 1,
                            range        : { min: 3, max: 10 },
                            required     : true,
                            defaultValue : 4,
                        },
                    },
                    [MirrorShapeId.RectangleMirror]: {
                        [FieldDimensionId.Length]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 50, max: 1000 },
                            required     : true,
                            defaultValue : 200,
                        },
                        [FieldDimensionId.Width]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 50, max: 1000 },
                            required     : true,
                            defaultValue : 200,
                        },
                        [FieldDimensionId.Thickness]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 1,
                            range        : { min: 3, max: 10 },
                            required     : true,
                            defaultValue : 4,
                        },
                    },
                    [MirrorShapeId.RoundMirror]: {
                        [FieldDimensionId.Diameter]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 50, max: 1000 },
                            required     : true,
                            defaultValue : 200,
                        },
                        [FieldDimensionId.Thickness]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 1,
                            range        : { min: 3, max: 10 },
                            required     : true,
                            defaultValue : 4,
                        },
                    },
                    [MirrorShapeId.OvalMirror]: {
                        [FieldDimensionId.Length]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 50, max: 1000 },
                            required     : true,
                            defaultValue : 200,
                        },
                        [FieldDimensionId.Width]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 50, max: 1000 },
                            required     : true,
                            defaultValue : 200,
                        },
                        [FieldDimensionId.Thickness]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 1,
                            range        : { min: 3, max: 10 },
                            required     : true,
                            defaultValue : 4,
                        },
                    },
                },
            },
        },
        material: {
            material: {
                value   : undefined,
                options : [
                    ...allMirror,
                ],
                required: true,
            },
        },
        additionalData: additionalDataFactory({
            budgetMin          : 500,
            budgetMax          : 5000,
            budgetDefaultValue : 1500,
        }),
        contact: fieldsContact,
    },
};

export default mirrorForm;
